import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { MapPin, Camera, Upload, X, Filter, CheckCircle, XCircle } from 'lucide-react';
import Layout from '../../components/Layout/Layout';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix iconos Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const COLORES = {
  contactado:    '#16a34a',
  no_encontrado: '#f59e0b',
  rechazo:       '#ef4444',
  pendiente:     '#3b82f6',
};

const COLORES_EVIDENCIA = {
  lona:    '#8b5cf6',
  barda:   '#ec4899',
  volante: '#06b6d4',
  cartel:  '#f97316',
  otro:    '#64748b',
};

// Roles que pueden ver todo y usar el filtro manual
const ROLES_FULL = ['superadmin', 'coordinador_general', 'coordinador_territorial', 'coordinador_sectorial'];
// Roles que pueden validar evidencias
const ROLES_VALIDAR = ['superadmin', 'coordinador_general'];
// Roles que SOLO ven su sección
const ROLES_SOLO_SU_SECCION = ['promotor', 'seccional', 'operador', 'responsable_inventario'];

// ── Componente mapa Leaflet ──────────────────────────────────────────────────
function MapaLeaflet({ id, markers, alturaClase = 'h-[350px] md:h-[600px]', poligonos = [], statsPorSeccion = {} }) {
  const mapRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!mapRef.current) return;
      if (instanceRef.current) {
        instanceRef.current.remove();
        instanceRef.current = null;
      }
      const mapa = L.map(mapRef.current).setView([19.9333, -97.9667], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap'
      }).addTo(mapa);
      instanceRef.current = mapa;

        let geoJsonLayer = null;
        if (poligonos && poligonos.length > 0) {
          geoJsonLayer = L.geoJSON(poligonos, {
            style: {
              fillColor: '#4ade80',
              fillOpacity: 0.25,
              color: '#16a34a',
              weight: 1,
              opacity: 0.5
            },
            onEachFeature: (feature, layer) => {
              const ns = feature.properties?.numero_seccion;
              const mun = feature.properties?.municipio;
              const stats = statsPorSeccion[ns];
              const pct = (stats && stats.meta_votos > 0)
                ? Math.min(Math.round((stats.promovidos_activos / stats.meta_votos) * 100), 100)
                : null;
              layer.bindPopup(`
                <div style="min-width:170px">
                  <p style="font-weight:bold;margin:0 0 4px">Sección ${ns || '—'}</p>
                  <p style="margin:0;font-size:12px;color:#555">${mun || ''}</p>
                  ${stats ? `
                    <p style="margin:6px 0 0;font-size:12px;color:#16a34a;font-weight:bold">${pct !== null ? pct + '% de avance' : 'Sin meta definida'}</p>
                    <p style="margin:2px 0 0;font-size:11px;color:#888">${stats.contactados || 0} contactados · ${stats.promovidos_activos || 0}/${stats.meta_votos || '—'} promovidos</p>
                  ` : '<p style="margin:6px 0 0;font-size:11px;color:#aaa">Sin datos de cobertura aún</p>'}
                </div>`);
            }
          });
          geoJsonLayer.addTo(mapa);
        }

      markers.forEach(m => {
        L.marker([m.lat, m.lng], { icon: m.icono })
          .addTo(mapa)
          .bindPopup(m.popup);
      });

      if (markers.length > 0 || (poligonos && poligonos.length > 0)) {
        const bounds = markers.length > 0 ? L.latLngBounds(markers.map(m => [m.lat, m.lng])) : geoJsonLayer.getBounds();
        mapa.fitBounds(bounds, { padding: [40, 40] });
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      if (instanceRef.current) {
        instanceRef.current.remove();
        instanceRef.current = null;
      }
    };
  }, [markers]);

  return (
    <div ref={mapRef} id={id}
      className={`w-full rounded-xl shadow-sm overflow-hidden ${alturaClase}`}
      style={{ zIndex: 0 }} />
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function CoberturaCampo() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rol = user?.rol || 'promotor';

  const puedeFiltar   = ROLES_FULL.includes(rol);
  const puedeValidar  = ROLES_VALIDAR.includes(rol);
  const soloEvidencias = rol === 'responsable_inventario';
  const esRolOperativo = ROLES_SOLO_SU_SECCION.includes(rol);

  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabActiva, setTabActiva] = useState(soloEvidencias ? 'evidencias' : 'mapa');
  const [filtroSeccion, setFiltroSeccion] = useState('');
  const [productos, setProductos] = useState([]);
  const [tiposEvidencia, setTiposEvidencia] = useState([]);
  const [mensajeValidacion, setMensajeValidacion] = useState(null);

  // Modal evidencia
  const [mostrarModal, setMostrarModal] = useState(false);
  const [ubicando, setUbicando] = useState(false);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [mensajeEvidencia, setMensajeEvidencia] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [formEvidencia, setFormEvidencia] = useState({
    tipo_evidencia: '', producto_id: '', seccion_id: '',
    observaciones: '', latitud: '', longitud: '', foto: null
  });

  useEffect(() => { fetchDatos(); }, []);

  const fetchDatos = async () => {
    try {
      setLoading(true);
      const [mapaRes, prodRes, tiposRes] = await Promise.all([
        api.get('/evidencias/mapa'),
        api.get('/inventario/productos'),
        api.get('/catalogos/tipos-evidencia')
      ]);
      setDatos(mapaRes.data);
      setProductos(prodRes.data.productos || []);
      setTiposEvidencia(tiposRes.data.tipos || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Validar evidencia ────────────────────────────────────────────────────
  const handleValidar = async (evidenciaId, nuevoEstatus) => {
    try {
      await api.put(`/evidencias/${evidenciaId}/validar`, { estatus_validacion: nuevoEstatus });
      setMensajeValidacion({
        tipo: 'exito',
        texto: nuevoEstatus === 'aprobada' ? '✅ Evidencia aprobada.' : '❌ Evidencia rechazada.'
      });
      fetchDatos();
      setTimeout(() => setMensajeValidacion(null), 3000);
    } catch (error) {
      setMensajeValidacion({ tipo: 'error', texto: 'Error al validar evidencia.' });
    }
  };

  const handleFiltroSeccion = (valor) => setFiltroSeccion(valor);

  const baseUrl = '';

  const visitasMostrar = puedeFiltar
    ? (datos?.visitas || []).filter(v => !filtroSeccion || v.numero_seccion === filtroSeccion)
    : (datos?.visitas || []);

  // Ocultar evidencias rechazadas de la lista principal (ya no se necesitan)
  const evidenciasMostrar = (datos?.evidencias || [])
    .filter(e => e.estatus_validacion !== 'rechazada')
    .filter(e => !puedeFiltar || !filtroSeccion || e.numero_seccion === filtroSeccion);

  // Usar las secciones que el backend ya filtró por rol/estructura
  const seccionesUnicas = (datos?.por_seccion || []).map(s => s.numero_seccion).filter(Boolean).sort();
  // Misma fuente, pero con id/municipio para el formulario de registrar evidencia
  const seccionesForm = (datos?.por_seccion || []).map(s => ({ id: s.id, numero_seccion: s.numero_seccion, municipio: s.municipio }));

    const poligonosSecciones = datos?.poligonos_secciones?.features || [];
    const statsPorSeccion = (datos?.por_seccion || []).reduce((acc, s) => {
      acc[s.numero_seccion] = s;
      return acc;
    }, {});

  const markersVisitas = visitasMostrar.map(v => {
    const color = COLORES[v.resultado_visita] || '#64748b';
    return {
      lat: parseFloat(v.latitud), lng: parseFloat(v.longitud),
      icono: L.divIcon({
        className: '',
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7]
      }),
      popup: `
        <div style="min-width:180px">
          <p style="font-weight:bold;margin:0 0 4px">${v.nombre ? `${v.nombre} ${v.apellido_paterno}` : 'Sin persona'}</p>
          <p style="margin:0;font-size:12px;color:#555">Sección: ${v.numero_seccion || '—'}</p>
          <p style="margin:2px 0;font-size:12px;color:${color};font-weight:bold">${v.resultado_visita?.replace('_', ' ')}</p>
          ${v.se_entrego_propaganda ? '<p style="margin:0;font-size:11px">📦 Con propaganda</p>' : ''}
          <p style="margin:4px 0 0;font-size:11px;color:#888">${new Date(v.fecha_hora).toLocaleDateString('es-MX')}</p>
        </div>`
    };
  });

  const markersEvidencias = evidenciasMostrar.map(e => {
    const color = COLORES_EVIDENCIA[e.tipo_evidencia] || '#64748b';
    return {
      lat: parseFloat(e.latitud), lng: parseFloat(e.longitud),
      icono: L.divIcon({
        className: '',
        html: `<div style="width:20px;height:20px;border-radius:4px;background:${color};border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:11px;">📷</div>`,
        iconSize: [20, 20], iconAnchor: [10, 10]
      }),
      popup: `
        <div style="min-width:200px">
          <p style="font-weight:bold;margin:0 0 4px;text-transform:capitalize">${e.tipo_evidencia}</p>
          <p style="margin:0;font-size:12px">${e.nombre_producto || '—'}</p>
          <p style="margin:2px 0;font-size:11px;color:#888">Sección: ${e.numero_seccion || '—'}</p>
          <img src="${baseUrl}${e.foto_url}" style="width:100%;border-radius:4px;margin-top:6px;max-height:120px;object-fit:cover"/>
          <p style="margin:4px 0 0;font-size:11px;color:#888">${new Date(e.fecha_hora).toLocaleDateString('es-MX')}</p>
        </div>`
    };
  });

  const obtenerUbicacion = () => {
    setUbicando(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormEvidencia(prev => ({
          ...prev,
          latitud: pos.coords.latitude.toString(),
          longitud: pos.coords.longitude.toString()
        }));
        setUbicando(false);
      },
      () => { alert('No se pudo obtener la ubicación.'); setUbicando(false); }
    );
  };

  const handleFoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormEvidencia(prev => ({ ...prev, foto: file }));
    setFotoPreview(URL.createObjectURL(file));
  };

  const handleSubirEvidencia = async (e) => {
    e.preventDefault();
    if (!formEvidencia.foto) { setMensajeEvidencia({ tipo: 'error', texto: 'La foto es obligatoria.' }); return; }
    if (!formEvidencia.latitud) { setMensajeEvidencia({ tipo: 'error', texto: 'Captura la ubicación GPS.' }); return; }
    setSubiendoFoto(true);
    try {
      const fd = new FormData();
      Object.entries(formEvidencia).forEach(([k, v]) => { if (v) fd.append(k, v); });
      await api.post('/evidencias', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMensajeEvidencia({ tipo: 'exito', texto: '✅ Evidencia registrada.' });
      fetchDatos();
      setTimeout(() => {
        setMostrarModal(false);
        setMensajeEvidencia(null);
        setFotoPreview(null);
        setFormEvidencia({ tipo_evidencia: '', producto_id: '', seccion_id: '', observaciones: '', latitud: '', longitud: '', foto: null });
      }, 1500);
    } catch (error) {
      setMensajeEvidencia({ tipo: 'error', texto: error.response?.data?.error || 'Error al subir.' });
    } finally {
      setSubiendoFoto(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Cargando cobertura...</p>
    </div>
  );

  return (
    <Layout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto w-full">

        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <MapPin className="text-green-600" size={28} /> Cobertura en Campo
            </h2>
            <p className="text-gray-500 text-sm">
              {esRolOperativo ? '📍 Mostrando datos de tu sección asignada' : 'Mapa de visitas, promovidos y material distribuido'}
            </p>
          </div>
          <button onClick={() => setMostrarModal(true)}
            className="text-white font-semibold px-4 py-2.5 rounded-xl bg-verde-600 hover:bg-verde-700 transition-all shadow-sm hover:shadow-md flex items-center gap-2">
            <Camera size={18} /> Registrar Evidencia
          </button>
        </div>

        {/* Mensaje de validación */}
        {mensajeValidacion && (
          <div className={`mb-4 p-3 rounded-xl text-sm font-medium ${
            mensajeValidacion.tipo === 'exito'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {mensajeValidacion.texto}
          </div>
        )}

        {/* TABS */}
        <div className="flex gap-2 mb-4 border-b border-gray-200 overflow-x-auto">
          {[
            { key: 'mapa',       label: '🗺 Mapa de Visitas',       visible: !soloEvidencias },
            { key: 'secciones',  label: '📊 Por Sección',           visible: !soloEvidencias },
            { key: 'evidencias', label: '📷 Evidencias de Material', visible: true },
          ].filter(t => t.visible).map(t => (
            <button key={t.key} onClick={() => setTabActiva(t.key)}
              className={`px-4 py-2 font-medium border-b-2 transition text-sm whitespace-nowrap ${
                tabActiva === t.key ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB MAPA DE VISITAS ── */}
        {tabActiva === 'mapa' && !soloEvidencias && (
          <div>
            <div className="bg-white rounded-xl shadow-sm p-3 mb-3 flex flex-wrap gap-4 text-xs items-center">
              <span className="font-semibold text-gray-700">Visitas:</span>
              {Object.entries(COLORES).map(([k, c]) => (
                <span key={k} className="flex items-center gap-1 font-medium capitalize" style={{ color: c }}>
                  ● {k.replace('_', ' ')}
                </span>
              ))}
            </div>
            {puedeFiltar && (
              <div className="flex items-center gap-3 mb-3">
                <Filter size={16} className="text-gray-500" />
                <select value={filtroSeccion} onChange={(e) => handleFiltroSeccion(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Todas las secciones</option>
                  {seccionesUnicas.map(s => <option key={s} value={s}>Sección {s}</option>)}
                </select>
                <span className="text-xs text-gray-500">{markersVisitas.length} visitas con GPS</span>
              </div>
            )}
            {esRolOperativo && (
              <p className="text-xs text-gray-500 mb-3">{markersVisitas.length} visitas con GPS en tu sección</p>
            )}
            {markersVisitas.length === 0 && poligonosSecciones.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <MapPin className="text-gray-300 mx-auto mb-3" size={48} />
                <p className="text-gray-500">No hay visitas con GPS registradas{esRolOperativo ? ' en tu sección' : ''}</p>
              </div>
            ) : (
              <MapaLeaflet
                key={`visitas-${filtroSeccion}-${markersVisitas.length}`}
                id="mapa-visitas" markers={markersVisitas}
                  poligonos={poligonosSecciones} statsPorSeccion={statsPorSeccion} />
            )}
          </div>
        )}

        {/* ── TAB POR SECCIÓN ── */}
        {tabActiva === 'secciones' && !soloEvidencias && (
          <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Sección</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Municipio</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Meta</th>
                  <th className="text-left py-3 px-4 font-medium text-green-600">✅ Contactados</th>
                  <th className="text-left py-3 px-4 font-medium text-yellow-600">⏳ No encontrados</th>
                  <th className="text-left py-3 px-4 font-medium text-red-600">❌ Rechazos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">Promovidos</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-600">% Avance</th>
                </tr>
              </thead>
              <tbody>
                {datos?.por_seccion?.map((s, i) => {
                  const pct = s.meta_votos > 0
                    ? Math.min(Math.round((s.promovidos_activos / s.meta_votos) * 100), 100) : 0;
                  const barWidth = s.promovidos_activos > 0 ? Math.max(pct, 3) : 0;
                  return (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-bold">{s.numero_seccion}</td>
                      <td className="py-3 px-4">{s.municipio}</td>
                      <td className="py-3 px-4">{s.meta_votos || '—'}</td>
                      <td className="py-3 px-4 text-green-600 font-bold">{s.contactados}</td>
                      <td className="py-3 px-4 text-yellow-600 font-bold">{s.no_encontrados}</td>
                      <td className="py-3 px-4 text-red-600 font-bold">{s.rechazos}</td>
                      <td className="py-3 px-4 font-bold text-gray-800">{s.promovidos_activos}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-3 overflow-hidden">
                            <div className="bg-green-500 h-3 rounded-full" style={{ width: `${barWidth}%` }} />
                          </div>
                          <span className="text-xs font-bold text-gray-700 min-w-[36px]">{pct}%</span>
                          <span className="text-xs text-gray-400">({s.promovidos_activos}/{s.meta_votos})</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── TAB EVIDENCIAS ── */}
        {tabActiva === 'evidencias' && (
          <div>
            <div className="bg-white rounded-xl shadow-sm p-3 mb-3 flex flex-wrap gap-4 text-xs items-center">
              <span className="font-semibold text-gray-700">Material:</span>
              {Object.entries(COLORES_EVIDENCIA).map(([k, c]) => (
                <span key={k} className="flex items-center gap-1 font-medium capitalize" style={{ color: c }}>■ {k}</span>
              ))}
            </div>

            {puedeFiltar && (
              <div className="flex items-center gap-3 mb-3">
                <Filter size={16} className="text-gray-500" />
                <select value={filtroSeccion} onChange={(e) => handleFiltroSeccion(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Todas las secciones</option>
                  {seccionesUnicas.map(s => <option key={s} value={s}>Sección {s}</option>)}
                </select>
                <span className="text-xs text-gray-500">{markersEvidencias.length} evidencias con GPS</span>
              </div>
            )}

            {esRolOperativo && (
              <p className="text-xs text-gray-500 mb-3">{markersEvidencias.length} evidencias en tu sección</p>
            )}

            {/* Indicador para superadmin */}
            {puedeValidar && evidenciasMostrar.filter(e => e.estatus_validacion === 'pendiente').length > 0 && (
              <div className="mb-3 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800 flex items-center gap-2">
                ⏳ Tienes <strong>{evidenciasMostrar.filter(e => e.estatus_validacion === 'pendiente').length}</strong> evidencia(s) pendiente(s) de validar
              </div>
            )}

            {/* Mapa de evidencias */}
            {markersEvidencias.length > 0 || poligonosSecciones.length > 0 ? (
              <div className="mb-6">
                <MapaLeaflet
                  key={`evidencias-${filtroSeccion}-${markersEvidencias.length}`}
                  id="mapa-evidencias" markers={markersEvidencias}
                    poligonos={poligonosSecciones} statsPorSeccion={statsPorSeccion} />
              </div>
            ) : (
              <div className="bg-white rounded-xl p-8 text-center mb-6">
                <MapPin className="text-gray-300 mx-auto mb-2" size={36} />
                <p className="text-gray-400 text-sm">No hay evidencias con GPS{esRolOperativo ? ' en tu sección' : ''}</p>
              </div>
            )}

            {/* Galería */}
            <h3 className="font-semibold text-gray-800 mb-3">📷 Galería de Evidencias</h3>
            {evidenciasMostrar.length === 0 ? (
              <div className="bg-white rounded-xl p-10 text-center">
                <Camera className="text-gray-300 mx-auto mb-3" size={48} />
                <p className="text-gray-500">No hay evidencias registradas{esRolOperativo ? ' en tu sección' : ' aún'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {evidenciasMostrar.map((e, i) => (
                  <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="relative">
                      <img src={`${baseUrl}${e.foto_url}`} alt={e.tipo_evidencia}
                        className="w-full h-48 object-cover" />
                      {/* Badge de estatus sobre la foto */}
                      <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold shadow ${
                        e.estatus_validacion === 'aprobada' ? 'bg-green-500 text-white' :
                        e.estatus_validacion === 'rechazada' ? 'bg-red-500 text-white' :
                        'bg-yellow-400 text-yellow-900'
                      }`}>
                        {e.estatus_validacion === 'aprobada' ? '✅ Aprobada' :
                         e.estatus_validacion === 'rechazada' ? '❌ Rechazada' :
                         '⏳ Pendiente'}
                      </span>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="capitalize font-semibold text-gray-800">{e.tipo_evidencia}</span>
                        <span className="text-xs text-gray-400">{new Date(e.fecha_hora).toLocaleDateString('es-MX')}</span>
                      </div>
                      <p className="text-xs text-gray-500">{e.nombre_producto || '—'}</p>
                      <p className="text-xs text-gray-500 mb-2">Sección: {e.numero_seccion || '—'}</p>
                      {e.latitud && (
                        <a href={`https://maps.google.com/?q=${e.latitud},${e.longitud}`}
                          target="_blank" rel="noreferrer"
                          className="text-blue-500 text-xs flex items-center gap-1 mb-3 hover:underline">
                          <MapPin size={12} /> Ver en Google Maps
                        </a>
                      )}

                      {/* Botones de validar — solo superadmin y coordinador_general */}
                      {puedeValidar && e.estatus_validacion !== 'aprobada' && (
                        <button
                          onClick={() => handleValidar(e.id, 'aprobada')}
                          className="w-full flex items-center justify-center gap-2 py-1.5 rounded-lg text-xs font-semibold bg-green-500 hover:bg-green-600 text-white transition mb-1">
                          <CheckCircle size={14} /> Aprobar evidencia
                        </button>
                      )}
                      {puedeValidar && e.estatus_validacion !== 'rechazada' && (
                        <button
                          onClick={() => handleValidar(e.id, 'rechazada')}
                          className="w-full flex items-center justify-center gap-2 py-1.5 rounded-lg text-xs font-semibold bg-red-500 hover:bg-red-600 text-white transition">
                          <XCircle size={14} /> Rechazar evidencia
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </div>

      {/* ── MODAL REGISTRAR EVIDENCIA ── */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-guinda-900 px-5 py-4 sticky top-0 z-10 flex items-center justify-between"
              style={{ borderRadius: '1rem 1rem 0 0' }}>
              <h3 className="text-white font-bold text-base md:text-lg flex items-center gap-2">
                <Camera size={20} className="text-guinda-200" />
                Registrar Evidencia de Material
              </h3>
              <button onClick={() => { setMostrarModal(false); setFotoPreview(null); setMensajeEvidencia(null); }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubirEvidencia} className="p-6 space-y-4">
              {mensajeEvidencia && (
                <div className={`p-3 rounded-lg text-sm ${
                  mensajeEvidencia.tipo === 'exito'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>{mensajeEvidencia.texto}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Evidencia *</label>
                <select value={formEvidencia.tipo_evidencia}
                  onChange={(e) => setFormEvidencia(prev => ({ ...prev, tipo_evidencia: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" required>
                  <option value="">Seleccionar...</option>
                  {tiposEvidencia.map(t => (
                    <option key={t.clave} value={t.clave}>{t.emoji} {t.etiqueta}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Producto de Inventario</label>
                <select value={formEvidencia.producto_id}
                  onChange={(e) => setFormEvidencia(prev => ({ ...prev, producto_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Seleccionar...</option>
                  {productos.map(p => <option key={p.id} value={p.id}>{p.nombre_producto}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral</label>
                <select value={formEvidencia.seccion_id}
                  onChange={(e) => setFormEvidencia(prev => ({ ...prev, seccion_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Seleccionar...</option>
                  {seccionesForm.map(s => <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Foto *</label>
                <label className="w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-green-400 transition">
                  {fotoPreview ? (
                    <img src={fotoPreview} alt="preview" className="w-full max-h-48 object-cover rounded-lg" />
                  ) : (
                    <>
                      <Upload size={32} className="text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Toca para tomar foto o seleccionar imagen</p>
                      <p className="text-xs text-gray-400">JPG, PNG o WEBP — máx 10MB</p>
                    </>
                  )}
                  <input type="file" accept="image/*" capture="environment"
                    onChange={handleFoto} className="hidden" />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">📍 Ubicación GPS *</label>
                <button type="button" onClick={obtenerUbicacion} disabled={ubicando}
                  className="w-full px-4 py-3 bg-verde-600 hover:bg-verde-700 text-white rounded-xl font-semibold transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-60">
                  <MapPin size={18} />
                  {ubicando ? 'Obteniendo ubicación...'
                    : formEvidencia.latitud
                      ? `✅ ${parseFloat(formEvidencia.latitud).toFixed(4)}, ${parseFloat(formEvidencia.longitud).toFixed(4)}`
                      : 'Capturar ubicación actual'}
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea value={formEvidencia.observaciones}
                  onChange={(e) => setFormEvidencia(prev => ({ ...prev, observaciones: e.target.value }))}
                  rows={2} placeholder="Ej: Lona colocada en esquina principal..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button"
                  onClick={() => { setMostrarModal(false); setFotoPreview(null); setMensajeEvidencia(null); }}
                  className="flex-1 px-4 py-3 border border-ink-200 rounded-xl text-ink-700 font-semibold hover:bg-ink-50 transition">
                  Cancelar
                </button>
                <button type="submit" disabled={subiendoFoto}
                  className="flex-1 text-white font-semibold py-3 rounded-xl bg-verde-600 hover:bg-verde-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-2">
                  {subiendoFoto ? 'Subiendo...' : <><Camera size={16} /> Registrar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
